import { Main, Models, DataTable, Pagination, StatusBadge, SearchInput } from 'utils/imports.utils';
import { IDataTableColumn } from 'common_components/ui/data_table/data_table.ui';
import SelectDropdown from 'common_components/ui/select_dropdown/select_dropdown.ui';
import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSetState, buildAdminListQuery, getDropdownData, getDropdownValue, getAdminUserLabel } from 'utils/functions.utils';
import { AdminTicketRow } from 'interfaces/admin.interface';
import AdminPage from 'common_components/ui/admin_page/admin_page.ui';
import FilterChips from 'common_components/ui/filter_chips/filter_chips.ui';
import './admin_tickets.screen.scss';

const PAGE_SIZE = 20;

const STATUS_CHIPS = [
  { value: '', label: 'All' },
  { value: 'open', label: 'Open' },
  { value: 'in_progress', label: 'In progress' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'closed', label: 'Closed' },
];
const PRIORITY_OPTIONS = ['low', 'medium', 'high', 'urgent'];
const CATEGORY_OPTIONS = ['billing', 'technical', 'account', 'ar_experience', 'other'];

const AdminTickets = () => {
  const navigate = useNavigate();
  const hasLoadedOnce = useRef(false);
  const [state, setState] = useSetState({
    tickets: [] as AdminTicketRow[],
    totalDocs: 0,
    page: 1,
    search: '',
    statusFilter: '',
    priorityFilter: '',
    categoryFilter: '',
    loading: true,
  });

  const fetchTickets = async () => {
    if (!hasLoadedOnce.current) {
      setState({ loading: true });
    }
    try {
      const query = buildAdminListQuery({
        skip: (state.page - 1) * PAGE_SIZE,
        limit: PAGE_SIZE,
        search: state.search,
        status: state.statusFilter,
        priority: state.priorityFilter,
        category: state.categoryFilter,
      });
      const response: any = await Models.auth.adminListTickets(query);
      setState({
        tickets: response?.data?.docs || [],
        totalDocs: response?.data?.totalDocs || 0,
        loading: false,
      });
    } catch (err) {
      console.error('Failed to fetch tickets', err);
      setState({ loading: false });
    } finally {
      hasLoadedOnce.current = true;
    }
  };

  useEffect(() => {
    let token = localStorage.getItem('token');
    if (token) {
      fetchTickets();
    } else {
      navigate('/login');
    }
  }, [state.page, state.search, state.statusFilter, state.priorityFilter, state.categoryFilter]);

  const columns: IDataTableColumn[] = [
    { key: 'ticket_number', label: 'Ticket #' },
    { key: 'subject', label: 'Subject' },
    {
      key: 'user',
      label: 'From',
      render: (row: AdminTicketRow) => (row.user && getAdminUserLabel(row.user)) || '—',
    },
    {
      key: 'status',
      label: 'Status',
      render: (row: AdminTicketRow) =>
        row.status ? <StatusBadge status={row.status} /> : '—',
    },
    {
      key: 'created_at',
      label: 'Opened',
      align: 'right',
      render: (row: AdminTicketRow) =>
        row.created_at ? new Date(row.created_at).toLocaleDateString() : '—',
    },
  ];

  return (
    <Main backgroundColor="#F7F3E9" isRenderOnlyLogo={false} isAdmin>
      <AdminPage breadcrumb="Admin / Tickets" title="Support tickets">
        <div className="admin_tickets">
          <div className="admin_tickets_toolbar">
            <div className="admin_tickets_search">
              <SearchInput
                onSearch={(value) => setState({ search: value, page: 1 })}
                placeholder="Search tickets"
              />
            </div>
            <FilterChips
              options={STATUS_CHIPS}
              value={state.statusFilter}
              onChange={(value) => setState({ statusFilter: value, page: 1 })}
            />
          </div>
          <div className="admin_tickets_filters">
          <SelectDropdown
            data={getDropdownData(PRIORITY_OPTIONS)}
            value={state.priorityFilter ? getDropdownValue(state.priorityFilter) : null}
            onChange={(selectedOption: any) => setState({ priorityFilter: selectedOption?.value || '', page: 1 })}
            placeholder="All priorities"
            isFilter
            width="160px"
            height="40px"
          />
          <SelectDropdown
            data={getDropdownData(CATEGORY_OPTIONS)}
            value={state.categoryFilter ? getDropdownValue(state.categoryFilter) : null}
            onChange={(selectedOption: any) => setState({ categoryFilter: selectedOption?.value || '', page: 1 })}
            placeholder="All categories"
            isFilter
            width="160px"
            height="40px"
          />
          </div>
          <div className="admin_tickets_card">
            <DataTable
              columns={columns}
              rows={state.tickets}
              rowKey="_id"
              loading={state.loading}
              emptyMessage="No support tickets found"
              onRowClick={(row: AdminTicketRow) => navigate(`/admin/tickets/${row._id}`)}
            />
          </div>
          <Pagination
            currentPage={state.page}
            totalPages={Math.ceil(state.totalDocs / PAGE_SIZE)}
            pageSize={PAGE_SIZE}
            totalItems={state.totalDocs}
            onPageChange={(page) => setState({ page })}
          />
        </div>
      </AdminPage>
    </Main>
  );
};

export default AdminTickets;
